import { z } from 'zod';

/**
 * Case Wiki — taxonomy of procedural side (camp procédural d'une pièce).
 *
 * Axe orthogonal à `piece_nature` (qui dit "ce que c'est") et à
 * `document_category` (qui dit "d'où ça vient" — nos pièces / procédure /
 * partie adverse / etc., catégorie utilisée pour le rangement des PDF).
 *
 * `procedural_side` répond à la question : "qui a produit cette pièce
 * dans la procédure ?". Indispensable pour afficher la timeline des
 * écritures (page Écritures du dashboard Case Wiki) qui répartit les
 * pièces sur 2 rails (nos écritures / adverses) avec les décisions du
 * juge centrées sur le spine.
 *
 * Source unique consommée par :
 *   - le prompt système ingest (instructions à l'agent pour le déduire)
 *   - le tool `finish_ingest` (payload de retour, persisté en DB)
 *   - le filtre / rendu rail dans la page Écritures
 *
 * Schema source : migration M069
 * (`apps/backend/database/migrations/069_add_procedural_side_to_case_documents.sql`).
 *
 * Cf `docs/backlog/DEBT-131-case-documents-procedural-side.md`.
 */
export const ProceduralSideSchema = z.enum([
  /**
   * Notre client / nos avocats l'ont produite.
   * Exemples : notre assignation, nos conclusions n°1 et n°2, nos
   * mises en demeure pré-procédure quand on agit pour le créancier.
   */
  'our_party',
  /**
   * L'adversaire l'a produite.
   * Exemples : conclusions adverse, requête en intervention de
   * l'adversaire, leur signification.
   */
  'opposing_party',
  /**
   * Le juge l'a produite.
   * Exemples : jugement, arrêt, ordonnance. Une décision unilatérale émise
   * par une administration suit le camp de cette autorité, pas `court`.
   */
  'court',
  /**
   * Hors procédure ou camp non identifiable.
   * Exemples : contrat de bail signé par les 2 parties, mise en demeure
   * pré-procédure ambiguë, factures, témoignages, expertises.
   */
  'neutral',
]);
export type ProceduralSide = z.infer<typeof ProceduralSideSchema>;

/**
 * Libellés humains pour l'UI (page Écritures, badges).
 */
export const PROCEDURAL_SIDE_LABELS: Record<ProceduralSide, string> = {
  // Renommé "Notre camp" → "Notre client" (PR-E2c3, demande user 2026-06-01).
  // Sur les écritures, "Notre client" = la partie qu'on représente, pas
  // l'avocat lui-même. Cohérent avec EntitySide.ours dans shared.tsx.
  our_party: 'Notre client',
  opposing_party: 'Adverse',
  court: 'Juge',
  neutral: 'Neutre',
};
